import React from 'react';
import Calendar from 'react-calendar';
import { COLORS, LAYOUT } from '../../constants';
import 'react-calendar/dist/Calendar.css';

const calendarStyles = `
  .react-calendar {
    width: 100%;
    border: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  }
  
  .react-calendar__navigation {
    display: flex;
    height: 60px;
    margin-bottom: ${LAYOUT.spacing.md}px;
    background: linear-gradient(135deg, ${COLORS.primary} 0%, #9d0d1d 100%);
    border-radius: 12px;
  }
  
  .react-calendar__navigation button {
    color: white;
    font-size: 18px;
    font-weight: 600;
    min-width: 44px;
    background: none;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .react-calendar__navigation button:enabled:hover,
  .react-calendar__navigation button:enabled:focus {
    background-color: rgba(255, 255, 255, 0.15);
  }
  
  .react-calendar__navigation button:disabled {
    background-color: transparent;
    color: rgba(255, 255, 255, 0.5);
  }
  
  .react-calendar__month-view__weekdays {
    text-align: center;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    color: ${COLORS.text.secondary};
    padding: ${LAYOUT.spacing.sm}px 0;
  }
  
  .react-calendar__month-view__weekdays__weekday {
    padding: ${LAYOUT.spacing.sm}px;
  }
  
  .react-calendar__tile {
    max-width: 100%;
    padding: ${LAYOUT.spacing.md}px;
    background: none;
    text-align: center;
    line-height: 24px;
    font-size: 15px;
    border-radius: 8px;
    transition: all 0.2s ease;
    cursor: pointer;
    border: 2px solid transparent;
  }
  
  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background-color: ${COLORS.primary}10;
    border-color: ${COLORS.primary}30;
  }
  
  .react-calendar__tile--now {
    background: ${COLORS.primary}08;
    border-color: ${COLORS.primary}30;
    font-weight: 600;
  }
  
  .react-calendar__tile--now:enabled:hover,
  .react-calendar__tile--now:enabled:focus {
    background: ${COLORS.primary}15;
    border-color: ${COLORS.primary}50;
  }
  
  .react-calendar__tile--active {
    background: ${COLORS.primary} !important;
    color: white !important;
    font-weight: 700;
    border-color: ${COLORS.primary} !important;
  }
  
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: #9d0d1d !important;
    border-color: #9d0d1d !important;
  }
  
  .react-calendar__month-view__days__day--neighboringMonth {
    color: ${COLORS.text.muted};
    opacity: 0.5;
  }
  
  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    padding: ${LAYOUT.spacing.lg}px;
  }
`;

const CalendarView = ({ selectedDate, onDateSelect, eventsByDate, latestMeetingDate }) => {
  
  // Custom tile content to show meeting indicators
  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    
    const dateStr = date.toISOString().slice(0, 10);
    const dayEvents = eventsByDate[dateStr] || [];
    const eventCount = dayEvents.length;
    const isLatestMeeting = dateStr === latestMeetingDate;
    
    if (eventCount === 0) return null;
    
    return (
      <div className="calendar-indicators">
        {/* Meeting count dot */}
        <div className="meeting-count-dot">
          {eventCount}
        </div>
        
        {/* Latest meeting indicator */}
        {isLatestMeeting && (
          <div className="latest-meeting-indicator">
            <div className="new-meeting-star">★</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <style>{calendarStyles}</style>
      <style>{`
        .calendar-indicators {
          position: absolute;
          top: 2px;
          right: 2px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
          pointer-events: none;
        }
        
        .meeting-count-dot {
          background: ${COLORS.primary};
          color: white;
          border-radius: 50%;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          font-weight: 700;
          line-height: 1;
          box-shadow: 0 2px 4px rgba(206, 17, 38, 0.3);
          animation: meetingDotPulse 2s ease-in-out infinite;
        }
        
        .latest-meeting-indicator {
          position: relative;
        }
        
        .new-meeting-star {
          color: #CE1126;
          font-size: 10px;
          font-weight: bold;
          text-shadow: 0 0 3px rgba(206, 17, 38, 0.5);
          animation: starPulse 2s ease-in-out infinite;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .react-calendar__tile {
          position: relative;
          min-height: 50px;
        }
        
        /* Enhanced styles for days with meetings */
        .react-calendar__tile:has(.calendar-indicators) {
          background: linear-gradient(135deg, ${COLORS.primary}05, ${COLORS.primary}10);
          border-color: ${COLORS.primary}20;
          box-shadow: 0 2px 8px rgba(206, 17, 38, 0.1);
        }
        
        .react-calendar__tile:has(.calendar-indicators):hover {
          background: linear-gradient(135deg, ${COLORS.primary}10, ${COLORS.primary}20);
          border-color: ${COLORS.primary}40;
          box-shadow: 0 4px 16px rgba(206, 17, 38, 0.2);
          transform: translateY(-1px);
        }
        
        .react-calendar__tile:has(.calendar-indicators):hover .meeting-count-dot {
          transform: scale(1.2);
          box-shadow: 0 3px 8px rgba(206, 17, 38, 0.4);
        }
        
        .react-calendar__tile:has(.calendar-indicators):hover .new-meeting-star {
          transform: scale(1.2);
          text-shadow: 0 0 5px rgba(206, 17, 38, 0.8);
        }
        
        @keyframes meetingDotPulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.1);
            opacity: 0.9;
          }
        }
        
        @keyframes starPulse {
          0%, 100% { 
            opacity: 1;
            transform: scale(1);
            text-shadow: 0 0 3px rgba(206, 17, 38, 0.5);
          }
          50% { 
            opacity: 0.7;
            transform: scale(1.1);
            text-shadow: 0 0 5px rgba(206, 17, 38, 0.8);
          }
        }
        
        @media (max-width: 768px) {
          .meeting-count-dot {
            width: 16px;
            height: 16px;
            font-size: 9px;
          }
          
          .new-meeting-star {
            font-size: 8px;
          }
        }
      `}</style>
      <Calendar 
        value={selectedDate} 
        onClickDay={onDateSelect}
        locale="en-US"
        tileContent={tileContent}
      />
    </>
  );
};

export default CalendarView;